import { showToast } from "@/components/ui/sonner";

import { ApiError } from "./error";

/**
 * Human-readable labels for the quota keys the backend enforces (mirrors
 * `quota_definitions`). Used to build a friendly "you hit your limit" message
 * from the verbose 429 payload.
 */
const QUOTA_LABELS: Record<string, string> = {
  searches: "total searches",
  arbitrator_runs: "total Arbitrator runs",
  profile_views: "profile views",
  pages_per_search: "result pages for this search",
  filter_changes_per_search: "filter changes for this search",
  insights: "insights",
  exports: "exports",
  sequences: "sequences",
  jobs: "jobs",
  projects: "projects",
  sourcing_agents: "sourcing agents",
  mailboxes: "mailboxes",
  linkedin_accounts: "Social accounts",
  credits: "credits",
};

/** A 429 raised by quota enforcement (`{ code: "QUOTA_EXCEEDED", ... }`). */
export function isQuotaError(error: unknown): error is ApiError {
  return (
    error instanceof ApiError &&
    error.status === 429 &&
    error.code === "QUOTA_EXCEEDED"
  );
}

/**
 * Any HTTP 429 — the plan quota (`QUOTA_EXCEEDED`) or the per-minute rate
 * limit (`RATE_LIMIT_EXCEEDED`). Both mean "you've hit a limit, back off",
 * as opposed to a genuine failure or an empty result. Callers that render
 * different UI for "limit reached" vs "no data" branch on this.
 */
export function isRateLimitedError(error: unknown): error is ApiError {
  return error instanceof ApiError && error.status === 429;
}

// Dedupe rapid-fire toasts (e.g. a burst of parallel requests all 429).
let lastQuotaToast = 0;

/**
 * Surface a quota-exceeded error to the user as a toast with an upgrade CTA.
 * Reads the verbose fields the backend returns (`quota_key`, `limit`,
 * `current`) to build a specific message.
 */
export function showQuotaExceededToast(error: ApiError): void {
  const now = Date.now();
  if (now - lastQuotaToast < 3000) return;
  lastQuotaToast = now;

  const detail = error.detail ?? {};
  const quotaKey = typeof detail.quota_key === "string" ? detail.quota_key : "";
  const label = QUOTA_LABELS[quotaKey] ?? "plan limit";
  const limit = typeof detail.limit === "number" ? detail.limit : null;

  const title = error.message || "You've reached your plan limit";
  const description =
    limit != null
      ? `Your current plan includes ${limit} ${label}. Upgrade your plan to keep going.`
      : `You've reached your ${label}. Upgrade your plan to keep going.`;

  showToast.warning(title, {
    description,
    duration: 6000,
    action: {
      label: "View plans",
      onClick: () => {
        window.location.href = "/settings/plans-billing";
      },
    },
  });
}

/**
 * If `error` is a quota error, toast it and return true; otherwise return
 * false so callers can fall through to their own handling.
 */
export function handleQuotaError(error: unknown): boolean {
  if (!isQuotaError(error)) return false;
  showQuotaExceededToast(error);
  return true;
}
